import type { FC, ReactNode } from 'react'
import type { Locale } from '@/lib/i18n/config'
import type { Formatter } from '@/lib/format'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo
} from 'react'

import { useRouter } from 'next/router'
import { createFormatter } from '@/lib/format'
import { intlTag, toLocale } from '@/lib/i18n/config'

interface I18nValue {
    locale: Locale
    fmt: Formatter
    setLocale: (locale: Locale) => void
}

interface I18nProviderProps {
    children: ReactNode
}

const I18nContext = createContext<I18nValue | null>(null)

/**
 * The active language comes from the route (/ is Spanish, /en is English),
 * so the toggle in the sidebar and the preview language switcher agree.
 */
export const I18nProvider: FC<I18nProviderProps> = ({ children }): ReactNode => {
    const router = useRouter()
    const locale = toLocale(router.locale)

    const setLocale = useCallback((next: Locale) => {
        if (next === locale) return

        void router.push({ pathname: router.pathname, query: router.query }, router.asPath, { locale: next, scroll: false })
    }, [locale, router])

    useEffect(() => {
        document.documentElement.lang = intlTag(locale)
    }, [locale])

    const value = useMemo<I18nValue>(() => ({ locale, fmt: createFormatter(locale), setLocale }), [locale, setLocale])

    return (
        <I18nContext.Provider value={value}>
            {children}
        </I18nContext.Provider>
    )
}

export const useI18n = (): I18nValue => {
    const value = useContext(I18nContext)

    if (!value) throw new Error('useI18n must be used inside I18nProvider')

    return value
}
